import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import DataGrideView from "../../../Component/DataGrideView/DataGrideView";
import { ToastShow } from "../../../Component/Toast/Toast";
import { GET_CITY_REQUESTS, GET_CITY_REQUEST_DETAILS } from "../../../Apis/city_api";

const formatDate = (value) => {
  const d = new Date(String(value ?? "").replace(" ", "T"));
  return isNaN(d.getTime()) ? "-" : format(d, "dd MMM yyyy, hh:mm a");
};

/**
 * Cities → "City Requests": cities where POD is not available and customers tapped
 * "Notify Me When Available" in the user app. City names come from the map (e.g. "Bengaluru Urban").
 */
function CityRequests() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);

  const getData = async () => {
    setIsLoading(true);
    const res = await GET_CITY_REQUESTS({ search, fromDate, toDate });
    if (res && res.s == 1) {
      setRows(res.r ?? []);
    } else {
      setRows([]);
      ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(getData, 400); // small delay while typing in search
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fromDate, toDate]);

  const totalRequests = rows.reduce((a, r) => a + Number(r.requests || 0), 0);

  const columns = [
    {
      field: "sr",
      headerName: "Sr.",
      width: 70,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.row.city) + 1,
    },
    { field: "city", headerName: "City (from map)", flex: 1, minWidth: 200 },
    {
      field: "requests",
      headerName: "Requests",
      width: 120,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "customers",
      headerName: "Customers",
      width: 120,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "first_request",
      headerName: "First request",
      width: 190,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "latest_request",
      headerName: "Latest request",
      width: 190,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "action",
      headerName: "Customers",
      width: 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setSelectedCity(params.row)}
          sx={{ textTransform: "none", color: "#F8AF41", borderColor: "#F8AF41" }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          bgcolor: "white",
          borderRadius: "5px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxShadow: "3px 3px 7px 0px #00000040",
        }}
      >
        <Box
          width="97%"
          marginY=".8rem"
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}
        >
          <IconButton size="small" onClick={() => navigate("/cities/list")}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ color: "#454545", fontSize: "1.2rem", fontWeight: "500" }}>
              City Requests
            </Typography>
            <Typography fontSize="12px" color="#7A7A7A">
              Cities where POD is not available yet and customers asked to be notified
              {rows.length ? ` · ${rows.length} cities · ${totalRequests} requests` : ""}
            </Typography>
          </Box>
          <TextField
            size="small"
            label="Search city"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Box>

        <Box width="98%" mb="1rem">
          <DataGrideView
            isLoading={isLoading}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.city}
            colHeight="3rem"
            rowsLimit={25}
            sx={{ height: "70vh" }}
          />
        </Box>
      </Box>

      {selectedCity ? (
        <CityRequestCustomers
          city={selectedCity}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setSelectedCity(null)}
        />
      ) : null}
    </Box>
  );
}

/** Popup: customers who requested the selected city (latest first). */
function CityRequestCustomers({ city, fromDate, toDate, onClose }) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const load = async (count) => {
    setIsLoading(true);
    const res = await GET_CITY_REQUEST_DETAILS({ city: city.city, fromDate, toDate, count });
    if (res && res.s == 1) {
      const list = res.r ?? [];
      setRows((prev) => (count === 0 ? list : [...prev, ...list]));
      setHasMore(list.length === 30);
    } else {
      setHasMore(false);
      ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city.city]);

  const columns = [
    {
      field: "sr",
      headerName: "Sr.",
      width: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.row.id) + 1,
    },
    {
      field: "name",
      headerName: "Customer",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography fontSize="13px" py="0.4rem" sx={{ whiteSpace: "normal" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Requested on",
      width: 190,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: "3rem" }}>
        <Box>
          <Typography fontSize="1.1rem" fontWeight="600">
            {city.city}
          </Typography>
          <Typography fontSize="12px" color="#7A7A7A">
            {city.requests} requests · {city.customers} customers
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <DataGrideView
          isLoading={isLoading}
          rows={rows}
          columns={columns}
          colHeight="3rem"
          rowsLimit={10}
          callBack={(loaded) => {
            if (loaded >= rows.length && hasMore && !isLoading) {
              load(rows.length);
            }
          }}
          sx={{ height: "60vh" }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default CityRequests;
